import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Page from 'src/components/Page';
import { HeaderPageComponentConfig } from 'src/components/_dashboard/configuracoes';
import { TableBorder } from 'src/components/table';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { UserListHead } from 'src/components/_dashboard/user/list';

// Shadcn Components
import { Card, CardContent } from 'src/@/components/ui/card';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Calendar } from 'src/@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import { CalendarIcon, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Utils
import { fDateTimeSuffix } from 'src/utils/formatTime';
import { fCurrency } from 'src/utils/formatNumber';

// Hooks
import useAuth from 'src/hooks/useAuth';
import useTabs from 'src/hooks/useTabs';

// Actions
import actionListaCampanhas from 'src/actions/campanhas/actionListaCampanhas';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import v2_actionCriarCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionCriarCampanhaRastreamento';
import v2_actionAtualizarCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionAtualizarCampanhaRastreamento';
import v2_actionCampanhasRastreamento from 'src/actions/v2/marketing/v2_actionCampanhasRastreamento';
import v2_actionExcluirCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionExcluirCampanhaRastreamento';

// Redux
import { useDispatch, useSelector } from 'src/redux/store';
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: null, label: "Todas" },
  { value: 'ativa', label: "Ativas" },
  { value: 'pausada', label: "Pausadas" },
  { value: 'finalizada', label: "Finalizadas" },
  { value: 'erro', label: "Com Erro" }
];

const PLATFORM_OPTIONS = [
  { value: 'all', label: "Todas as Plataformas" },
  { value: 'facebook', label: "Facebook Ads" },
  { value: 'google', label: "Google Ads" },
  { value: 'instagram', label: "Instagram Ads" }
];

const TABLE_HEAD = [
  { id: 'nome', label: 'Nome da Campanha', alignRight: false },
  { id: 'plataforma', label: 'Plataforma', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'data_inicio', label: 'Data Início', alignRight: false },
  { id: 'data_fim', label: 'Data Fim', alignRight: false },
  { id: 'acoes', label: 'Ações', alignRight: false },
];

// ----------------------------------------------------------------------

export default function MarketingRastreamento() {
  const dispatch = useDispatch();
  const { user, cliente } = useAuth();
  const navigate = useNavigate();

  // Estados
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('nome');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // Estados para filtros
  const [clienteIdPadrao, setClienteIdPadrao] = useState(user.cliente_id_padrao);
  const [listaClientesCanais, setListaClientesCanais] = useState([]);
  const [dataInicio, setDataInicio] = useState(new Date(2024, 0, 1));
  const [dataFinal, setDataFinal] = useState(new Date(new Date().getFullYear(), 11, 31));
  const [filtroPlataforma, setFiltroPlataforma] = useState(null);

  // Estados para modal de criação/edição de campanha
  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [campanhaEditando, setCampanhaEditando] = useState(null);
  const [salvandoCampanha, setSalvandoCampanha] = useState(false);

  // Estados para modal de exclusão
  const [modalExclusaoAberto, setModalExclusaoAberto] = useState(false);
  const [campanhaExcluindo, setCampanhaExcluindo] = useState(null);
  const [excluindoCampanha, setExcluindoCampanha] = useState(false);
  const [formularioCampanha, setFormularioCampanha] = useState({
    nome: '',
    plataforma: '',
    orcamento: '',
    dataInicio: new Date(),
    dataFim: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 dias a partir de hoje
    descricao: '',
    objetivo: '',
    // Configurações avançadas
    palavrasChave: [],
    novaPalavraChave: { type: 'contain', value: '' }
  });

  const isMultisession = cliente.multiSession === 1;
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs(null);

  // Buscar dados iniciais
  useEffect(() => {
    if (user.cliente_id_padrao) {
      setClienteIdPadrao(user.cliente_id_padrao);
    }
    if (cliente.multiSession === 1) {
      buscarListaClientesCanais();
    }
    buscarCampanhas(cliente._id);
  }, []);

  // Buscar campanhas quando as datas de filtro mudarem
  useEffect(() => {
    if (dataInicio && dataFinal) {
      buscarCampanhas(cliente._id);
    }
  }, [dataInicio, dataFinal]);

  // Funções
  const buscarListaClientesCanais = async () => {
    try {
      const response = await actionListaClientesCanais(cliente._id);
      setListaClientesCanais(response.data.data);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Erro ao buscar canais', { variant: 'error' });
    }
  };

  const buscarCampanhas = async (clienteId) => {
    try {
      setLoading(true);

      // Formatar datas para o formato esperado pela API
      const dataInicioFormatada = dataInicio ? dataInicio.toISOString().split('T')[0] : null;
      const dataFinalFormatada = dataFinal ? dataFinal.toISOString().split('T')[0] : null;

      // Construir query string com os parâmetros
      let queryParams = '';
      if (dataInicioFormatada && dataFinalFormatada) {
        queryParams = `?dataInicio=${dataInicioFormatada}&dataFinal=${dataFinalFormatada}`;
      }

      console.log('Buscando campanhas com parâmetros:', { clienteId, dataInicioFormatada, dataFinalFormatada });

      // Chamar a action para buscar campanhas reais
      const response = await v2_actionCampanhasRastreamento(clienteId, queryParams);

      if (response.data && response.data.error === false) {
        console.log('Campanhas carregadas:', response.data.data.length);
        console.log('Dados brutos da API:', response.data.data);

        // Mapear dados da API para o formato esperado pelo componente
        const campanhasMapeadas = response.data.data.map(campanha => ({
          _id: campanha._id,
          nome: campanha.name,
          plataforma: campanha.platform,
          status: campanha.status === 'active' ? 'ativa' : campanha.status,
          orcamento: parseFloat(campanha.config.budget) || 0,
          gasto: parseFloat(campanha.config.spent) || 0,
          impressoes: parseInt(campanha.config.impressions) || 0,
          cliques: parseInt(campanha.config.clicks) || 0,
          ctr: campanha.config.ctr || 0,
          cpc: parseFloat(campanha.config.cpc) || 0,
          conversoes: campanha.config.conversoes || 0,
          cpa: campanha.config.cpa || 0,
          roas: campanha.config.roas || 0,
          // Garantir datas válidas mesmo se a API não enviar start_date
          data_inicio: new Date(campanha.start_date || campanha.create_at || campanha.update_at),
          data_fim: new Date(campanha.end_date || campanha.update_at || campanha.create_at),
          criado_por: campanha.created_by,
          create_at: campanha.create_at,
          update_at: campanha.update_at,
          // Configurações importantes para edição
          objetivo: campanha.config.type || '',
          palavrasChave: campanha.config.keys || []
        }));

        console.log('Campanhas mapeadas:', campanhasMapeadas);
        setTableData(campanhasMapeadas);
      } else {
        console.log('Erro na resposta da API:', response.data);
        setTableData([]);
      }
    } catch (error) {
      console.log('Erro ao buscar campanhas:', error);
      setTableData([]);
      // enqueueSnackbar('Erro ao buscar campanhas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeClienteIdPadrao = (e) => {
    setClienteIdPadrao(e.target.value);
    buscarCampanhas(isMultisession ? e.target.value : cliente._id);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tableData.map((n) => n.nome);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVerDetalhes = (campanhaId, campanhaNome) => {
    // Navegar para a página de métricas com o ID da campanha
    navigate(`/dashboard/marketing/metricas?campanha=${campanhaId}&nome=${encodeURIComponent(campanhaNome)}`);
  };

  // Função para abrir modal de edição
  const handleEditarCampanha = (campanha) => {
    setModoEdicao(true);
    setCampanhaEditando(campanha);

    // Preencher formulário com dados da campanha
    setFormularioCampanha({
      nome: campanha.nome,
      plataforma: campanha.plataforma,
      orcamento: campanha.orcamento.toString(),
      dataInicio: new Date(campanha.data_inicio),
      dataFim: new Date(campanha.data_fim),
      descricao: '',
      objetivo: campanha.objetivo || '',
      palavrasChave: campanha.palavrasChave || [],
      novaPalavraChave: { type: 'contain', value: '' }
    });

    setModalAberto(true);
  };

  // Função para fechar modal e resetar estados
  const handleFecharModal = () => {
    setModalAberto(false);
    setModoEdicao(false);
    setCampanhaEditando(null);
    setSalvandoCampanha(false);
    setFormularioCampanha({
      nome: '',
      plataforma: '',
      orcamento: '',
      dataInicio: new Date(),
      dataFim: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      descricao: '',
      objetivo: '',
      palavrasChave: [],
      novaPalavraChave: { type: 'contain', value: '' }
    });
  };

  // Funções para exclusão de campanha
  const handleAbrirModalExclusao = (campanha) => {
    setCampanhaExcluindo(campanha);
    setModalExclusaoAberto(true);
  };

  const handleFecharModalExclusao = () => {
    setModalExclusaoAberto(false);
    setCampanhaExcluindo(null);
    setExcluindoCampanha(false);
  };

  const handleExcluirCampanha = async () => {
    if (!campanhaExcluindo) return;

    try {
      setExcluindoCampanha(true);

      const response = await v2_actionExcluirCampanhaRastreamento(
        cliente._id,
        campanhaExcluindo._id
      );

      if (response.data && response.data.error === false) {
        // Recarregar a lista de campanhas
        await buscarCampanhas(cliente._id);
        handleFecharModalExclusao();
        alert('Campanha excluída com sucesso!');
      } else {
        throw new Error(response.data?.mensagem || 'Erro ao excluir campanha');
      }
    } catch (error) {
      console.error('Erro ao excluir campanha:', error);
      alert('Erro ao excluir campanha. Tente novamente.');
    } finally {
      setExcluindoCampanha(false);
    }
  };

  // Funções para o modal de criação de campanha
  const handleInputChange = (campo, valor) => {
    setFormularioCampanha(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Funções para gerenciar palavras-chave
  const handleAdicionarPalavraChave = () => {
    if (formularioCampanha.novaPalavraChave.value.trim()) {
      setFormularioCampanha(prev => ({
        ...prev,
        palavrasChave: [...prev.palavrasChave, prev.novaPalavraChave],
        novaPalavraChave: { type: 'contain', value: '' }
      }));
    }
  };

  const handleRemoverPalavraChave = (index) => {
    setFormularioCampanha(prev => ({
      ...prev,
      palavrasChave: prev.palavrasChave.filter((_, i) => i !== index)
    }));
  };

  const handleTipoPalavraChave = (tipo) => {
    setFormularioCampanha(prev => ({
      ...prev,
      novaPalavraChave: { ...prev.novaPalavraChave, type: tipo }
    }));
  };

  const handleSalvarCampanha = async () => {
    try {
      setSalvandoCampanha(true);

      // Validação básica
      if (!formularioCampanha.nome || !formularioCampanha.plataforma) {
        alert('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      // Preparar dados para a API no formato correto
      const dadosCampanha = {
        name: formularioCampanha.nome,
        platform: formularioCampanha.plataforma,
        start_date: formularioCampanha.dataInicio,
        end_date: formularioCampanha.dataFim,
        created_by: user.id,
        status: 'active',
        config: {
          type: formularioCampanha.objetivo,
          budget: formularioCampanha.orcamento.toString() || "0",
          spent: modoEdicao ? campanhaEditando.gasto.toString() : "0",
          impressions: modoEdicao ? campanhaEditando.impressoes.toString() : "0",
          clicks: modoEdicao ? campanhaEditando.cliques.toString() : "0",
          cpc: modoEdicao ? campanhaEditando.cpc.toString() : "0",
          keys: formularioCampanha.palavrasChave
        },
        deleted: false
      };

      let data = {
        scope: dadosCampanha
      }

      console.log('Dados enviados para a API:', JSON.stringify(data, null, 2));

      let response;

      if (modoEdicao) {
        response = await v2_actionAtualizarCampanhaRastreamento(
          cliente._id,
          campanhaEditando._id,
          data
        );
      } else {
        // Chamar a action para criar a campanha
        response = await v2_actionCriarCampanhaRastreamento(
          cliente._id,
          data
        );
      }

      console.log('Resposta da API:', response.data);

      // Verificar se a resposta é válida
      if (!response || !response.data) {
        throw new Error('Nenhuma resposta recebida da API');
      }

      // Verificar diferentes formatos de resposta da API
      const isSuccess = response.data.success === true ||
        response.data.error === false ||
        (response.data.data && !response.data.error);

      if (isSuccess) {
        try {
          // Recarregar a lista de campanhas
          await buscarCampanhas(cliente._id);
        } catch (fetchError) {
          console.warn('Erro ao recarregar lista, mas campanha foi salva:', fetchError);
        }

        // Fechar modal e limpar formulário SEMPRE em caso de sucesso
        handleFecharModal();
        alert(modoEdicao ? 'Campanha atualizada com sucesso!' : 'Campanha criada com sucesso!');
      } else {
        // Erro específico da API
        const errorMessage = response.data.message || response.data.error || 'Erro desconhecido na API';
        throw new Error(`API Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);

      // Mostrar erro mais específico para o usuário
      let errorMsg = `Erro ao ${modoEdicao ? 'atualizar' : 'criar'} campanha`;

      if (error.message.includes('API Error:')) {
        errorMsg += ': ' + error.message.replace('API Error:', '');
      } else if (error.response && error.response.data) {
        errorMsg += `: ${error.response.data.message || error.response.data.error || 'Erro na API'}`;
      } else {
        errorMsg += '. Tente novamente.';
      }

      alert(errorMsg);

      // IMPORTANTE: Não fechar o modal em caso de erro para que o usuário possa corrigir
      // Modal só fecha em caso de sucesso (feito acima)
    } finally {
      setSalvandoCampanha(false);
    }
  };

  // Filtrar dados
  const dataFiltered = applySortFilter({
    tableData,
    filterStatus,
    filtroPlataforma,
    dataInicio,
    dataFinal
  });

  // Debug: verificar se mkcp está nos dados filtrados
  console.log('Dados filtrados:', dataFiltered.length);
  console.log('mkcp nos filtrados:', dataFiltered.find(c => c.nome === 'mkcp'));

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;
  const isUserNotFound = (!dataFiltered.length && !!filterStatus);

  return (

    <div className="container mx-auto px-4 py-8 max-w-7xl animate-in fade-in slide-in-from-bottom-2 duration-300">

      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Rastreamento de Campanhas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Acompanhe o desempenho das suas campanhas no Facebook Ads, Google Ads e Instagram
          </p>
        </div>

        <Dialog open={modalAberto} onOpenChange={setModalAberto}>
          <DialogTrigger asChild>
            <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-sm h-10 px-6 font-medium transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nova Campanha
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] rounded-2xl border-0 shadow-2xl bg-white p-0 overflow-hidden">
            <div className="p-6 pb-4 border-b border-gray-100 bg-gray-50/50">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  {modoEdicao ? 'Editar Campanha' : 'Criar Nova Campanha'}
                </DialogTitle>
              </DialogHeader>
            </div>

            <div className="p-6 grid gap-5">
              <div className="grid gap-2">
                <Label htmlFor="nome" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Nome da Campanha *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Campanha Black Friday 2024"
                  value={formularioCampanha.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-colors"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="plataforma" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Plataforma *</Label>
                <Select value={formularioCampanha.plataforma} onValueChange={(value) => handleInputChange('plataforma', value)}>
                  <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-colors">
                    <SelectValue placeholder="Selecione a plataforma" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                    <SelectItem value="facebook">Facebook Ads</SelectItem>
                    <SelectItem value="google">Google Ads</SelectItem>
                    <SelectItem value="instagram">Instagram Ads</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="objetivo" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Objetivo da Campanha</Label>
                <Select value={formularioCampanha.objetivo} onValueChange={(value) => handleInputChange('objetivo', value)}>
                  <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-colors">
                    <SelectValue placeholder="Selecione o objetivo" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                    <SelectItem value="conversao">Conversão</SelectItem>
                    <SelectItem value="alcance">Alcance</SelectItem>
                    <SelectItem value="engajamento">Engajamento</SelectItem>
                    <SelectItem value="trafico">Tráfego</SelectItem>
                    <SelectItem value="vendas">Vendas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="orcamento" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Orçamento (R$)</Label>
                <Input
                  id="orcamento"
                  type="number"
                  placeholder="Ex: 5000"
                  value={formularioCampanha.orcamento}
                  onChange={(e) => handleInputChange('orcamento', e.target.value)}
                  className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Data de Início</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-left font-normal rounded-xl border-gray-200 hover:bg-gray-50 hover:border-[#7C3AED]/50">
                        <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                        {formularioCampanha.dataInicio ? format(formularioCampanha.dataInicio, "dd/MM/yyyy", { locale: ptBR }) : <span className="text-gray-400">Selecione</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-xl border-gray-100 shadow-xl">
                      <Calendar
                        mode="single"
                        selected={formularioCampanha.dataInicio}
                        onSelect={(date) => handleInputChange('dataInicio', date)}
                        initialFocus
                        className="rounded-xl"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Data de Fim</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-left font-normal rounded-xl border-gray-200 hover:bg-gray-50 hover:border-[#7C3AED]/50">
                        <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                        {formularioCampanha.dataFim ? format(formularioCampanha.dataFim, "dd/MM/yyyy", { locale: ptBR }) : <span className="text-gray-400">Selecione</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-xl border-gray-100 shadow-xl">
                      <Calendar
                        mode="single"
                        selected={formularioCampanha.dataFim}
                        onSelect={(date) => handleInputChange('dataFim', date)}
                        initialFocus
                        className="rounded-xl"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Configurações Avançadas - Palavras-chave */}
              <div className="grid gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Palavras-chave</Label>
                <div className="border border-gray-200 rounded-xl p-4 space-y-4 bg-gray-50/30">
                  {/* Lista de palavras-chave adicionadas */}
                  {formularioCampanha.palavrasChave.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formularioCampanha.palavrasChave.map((palavra, index) => (
                        <div key={index} className="flex items-center gap-2 bg-white border border-gray-200 pl-3 pr-1 py-1 rounded-lg shadow-sm">
                          <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                            {palavra.type === 'exact' ? 'Exata' : 'Contém'}
                          </span>
                          <span className="text-sm font-medium text-gray-700 border-l border-gray-100 pl-2">{palavra.value}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoverPalavraChave(index)}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Adicionar nova palavra-chave */}
                  <div className="flex gap-2">
                    <Select
                      value={formularioCampanha.novaPalavraChave.type}
                      onValueChange={handleTipoPalavraChave}
                    >
                      <SelectTrigger className="w-28 rounded-xl border-gray-200 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                        <SelectItem value="contain">Contém</SelectItem>
                        <SelectItem value="exact">Exata</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex-1 flex gap-2">
                      <Input
                        placeholder="Digite a palavra-chave..."
                        value={formularioCampanha.novaPalavraChave.value}
                        onChange={(e) => setFormularioCampanha(prev => ({
                          ...prev,
                          novaPalavraChave: { ...prev.novaPalavraChave, value: e.target.value }
                        }))}
                        onKeyPress={(e) => e.key === 'Enter' && handleAdicionarPalavraChave()}
                        className="flex-1 rounded-xl border-gray-200 bg-white"
                      />
                      <Button
                        type="button"
                        onClick={handleAdicionarPalavraChave}
                        disabled={!formularioCampanha.novaPalavraChave.value.trim()}
                        className="rounded-xl bg-gray-900 text-white hover:bg-gray-800"
                      >
                        Adicionar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="flex justify-end gap-3 p-6 pt-2 border-t border-gray-100 bg-gray-50/30">
              <Button
                variant="ghost"
                onClick={handleFecharModal}
                disabled={salvandoCampanha}
                className="rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvarCampanha}
                disabled={salvandoCampanha}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-sm px-6"
              >
                {salvandoCampanha ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
                    {modoEdicao ? 'Salvando...' : 'Criando...'}
                  </div>
                ) : (
                  modoEdicao ? 'Salvar Alterações' : 'Criar Campanha'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmação de Exclusão */}
        <Dialog open={modalExclusaoAberto} onOpenChange={setModalExclusaoAberto}>
          <DialogContent className="sm:max-w-[400px] rounded-2xl border-0 shadow-2xl bg-white p-0 overflow-hidden">
            <div className="p-6 pb-4 border-b border-gray-100 bg-red-50/50">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Iconify icon="mdi:alert-circle-outline" width={24} height={24} className="text-red-500" />
                  Excluir Campanha
                </DialogTitle>
              </DialogHeader>
            </div>

            <div className="p-6">
              <p className="text-gray-600">
                Tem certeza que deseja excluir a campanha <span className="font-semibold text-gray-900">"{campanhaExcluindo?.nome}"</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Esta ação não pode ser desfeita. Todos os dados e eventos relacionados a esta campanha serão removidos permanentemente.
              </p>
            </div>

            <div className="flex justify-end gap-3 p-6 pt-2 border-t border-gray-100 bg-gray-50/30">
              <Button
                variant="ghost"
                onClick={handleFecharModalExclusao}
                disabled={excluindoCampanha}
                className="rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleExcluirCampanha}
                disabled={excluindoCampanha}
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-sm px-6"
              >
                {excluindoCampanha ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
                    Excluindo...
                  </div>
                ) : (
                  'Excluir Campanha'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center mb-6">
        <Card className="p-1 pr-3 rounded-2xl border border-gray-200 shadow-sm bg-white flex items-center gap-2 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="bg-gray-50 px-3 py-2 border-r border-gray-100 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Plataforma
          </div>
          <Select value={filtroPlataforma || 'all'} onValueChange={(value) => setFiltroPlataforma(value === 'all' ? null : value)}>
            <SelectTrigger className="w-48 border-0 shadow-none focus:ring-0 text-sm font-medium text-gray-700 h-9 p-0 px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-100 shadow-xl">
              {PLATFORM_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full relative">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                {TABLE_HEAD.map((headCell) => (
                  <th
                    key={headCell.id}
                    className="sticky top-0 z-10 bg-gray-50 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap shadow-sm"
                  >
                    {headCell.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {dataFiltered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const {
                    _id,
                    nome,
                    plataforma,
                    status,
                    orcamento,
                    gasto,
                    impressoes,
                    cliques,
                    ctr,
                    cpc,
                    conversoes,
                    cpa,
                    roas,
                    data_inicio,
                    data_fim
                  } = row;

                  const getPlataformaIcon = (plataforma) => {
                    switch (plataforma) {
                      case 'facebook':
                        return <Iconify icon="mdi:facebook" width={20} height={20} style={{ color: '#1877F2' }} />;
                      case 'google':
                        return <Iconify icon="mdi:google" width={20} height={20} style={{ color: '#4285F4' }} />;
                      case 'instagram':
                        return <Iconify icon="mdi:instagram" width={20} height={20} style={{ color: '#E4405F' }} />;
                      default:
                        return <Iconify icon="mdi:cellphone" width={20} height={20} className="text-gray-400" />;
                    }
                  };

                  const getPlataformaColor = (plataforma) => {
                    switch (plataforma) {
                      case 'facebook': return 'text-[#1877F2]';
                      case 'google': return 'text-[#4285F4]';
                      case 'instagram': return 'text-[#E4405F]';
                      default: return 'text-gray-500';
                    }
                  };

                  return (
                    <tr
                      key={_id}
                      className="hover:bg-gray-50/80 transition-colors duration-150 group"
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">{nome}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="bg-white p-1 rounded-lg border border-gray-100 shadow-sm">{getPlataformaIcon(plataforma)}</span>
                          <span className={`text-sm font-medium capitalize ${getPlataformaColor(plataforma)}`}>
                            {plataforma}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={`rounded-lg px-2.5 py-0.5 font-medium shadow-none border ${status === 'ativa' ? 'bg-green-50 text-green-700 border-green-100 hover:bg-green-100' :
                            status === 'pausada' ? 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100' :
                              status === 'finalizada' ? 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200' :
                                status === 'erro' ? 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100' :
                                  'bg-gray-50 text-gray-600 border-gray-100'
                            }`}
                        >
                          {status}
                        </Badge>
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {fDateTimeSuffix(new Date(data_inicio))}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {fDateTimeSuffix(new Date(data_fim))}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 opacity-100">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditarCampanha(row)}
                            className="h-8 w-8 p-0 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors"
                            title="Editar"
                          >
                            <Iconify icon="mdi:pencil" width={18} height={18} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleVerDetalhes(_id, nome)}
                            className="h-8 w-8 p-0 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors"
                            title="Ver Detalhes"
                          >
                            <Iconify icon="mdi:eye" width={18} height={18} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleAbrirModalExclusao(row)}
                            className="h-8 w-8 p-0 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Excluir"
                          >
                            <Iconify icon="mdi:trash-can-outline" width={18} height={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {emptyRows > 0 && (
                <tr style={{ height: 60 * emptyRows }}>
                  <td colSpan={TABLE_HEAD.length} />
                </tr>
              )}

              {(!dataFiltered.length && !loading) && (
                <tr>
                  <td colSpan={TABLE_HEAD.length} className="py-8 text-center">
                    <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                        <SearchNotFound searchQuery={filterStatus || ""} className="hidden" />
                        <Iconify icon="mdi:magnify-remove-outline" width={32} height={32} className="text-gray-300" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">Nenhuma campanha encontrada</h3>
                      <p className="max-w-sm mt-1 text-sm text-gray-400">
                        Tente ajustar os filtros ou crie uma nova campanha para começar.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="flex justify-between items-center p-4 border-t border-gray-100 bg-gray-50/30">
          <span className="text-sm text-gray-500">
            Mostrando <span className="font-medium">{page * rowsPerPage + 1}</span> - <span className="font-medium">{Math.min((page + 1) * rowsPerPage, dataFiltered.length)}</span> de <span className="font-medium">{dataFiltered.length}</span>
          </span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 uppercase font-semibold tracking-wider">Linhas:</span>
              <Select value={rowsPerPage.toString()} onValueChange={(value) => handleChangeRowsPerPage({ target: { value } })}>
                <SelectTrigger className="w-16 h-8 text-xs rounded-lg border-gray-200 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="h-8 w-8 p-0 rounded-lg border-gray-200"
              >
                <Iconify icon="mdi:chevron-left" width={20} height={20} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(Math.ceil(dataFiltered.length / rowsPerPage) - 1, page + 1))}
                disabled={page >= Math.ceil(dataFiltered.length / rowsPerPage) - 1}
                className="h-8 w-8 p-0 rounded-lg border-gray-200"
              >
                <Iconify icon="mdi:chevron-right" width={20} height={20} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, filterStatus, filtroPlataforma, dataInicio, dataFinal }) {
  let filtered = tableData;

  console.log('Aplicando filtros:', { filterStatus, filtroPlataforma, dataInicio, dataFinal });
  console.log('Dados antes do filtro:', tableData.length);

  // Filtrar por status
  if (filterStatus !== null) {
    filtered = filtered.filter((item) => item.status === filterStatus);
    console.log('Após filtro de status:', filtered.length);
  }

  // Filtrar por plataforma
  if (filtroPlataforma !== null) {
    filtered = filtered.filter((item) => item.plataforma === filtroPlataforma);
    console.log('Após filtro de plataforma:', filtered.length);
  }

  // Filtrar por período
  if (dataInicio && dataFinal) {
    filtered = filtered.filter((item) => {
      const itemDate = new Date(item.data_inicio);
      const isInRange = itemDate >= dataInicio && itemDate <= dataFinal;
      if (!isInRange && item.nome === 'mkcp') {
        console.log('mkcp filtrada por data:', { itemDate, dataInicio, dataFinal });
      }
      return isInRange;
    });
    console.log('Após filtro de período:', filtered.length);
  }

  console.log('Dados finais filtrados:', filtered.length);
  return filtered;
}
